/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Container, Card, Typography, Checkbox, FormControlLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField, IconButton, CircularProgress, Tooltip
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import RemoveCircleOutlineRounded from '@mui/icons-material/RemoveCircleOutlineRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import VerifiedUserRounded from '@mui/icons-material/VerifiedUserRounded';
import toast from 'react-hot-toast';
import moment from 'moment';
import { qualiopiApi } from '../../api/qualiopiApi';
import useMounted from '../../hooks/useMounted';
import { alpha } from '@mui/material/styles';
import { getTone, grey, lightVeil, darkVeil, greyAlpha } from '../../theme/tones';
import {
  CRITERES, INDICATEURS, STATUTS, STATUT_LABEL, STATUT_COULEUR, SOURCE_LABEL, SOURCE_COULEUR,
  indicateursApplicables, scoresParCritere, scoreGlobal
} from '../../utils/qualiopiReferentiel';

const token = () => localStorage.getItem('accessToken');

const scoreColor = (pct) => (pct >= 80 ? getTone('emerald').base : pct >= 50 ? getTone('amber').base : pct > 0 ? getTone('orange').base : grey(400));

// Anneau SVG de score : cercle de fond + arc de progression (strokeDashoffset), départ à midi.
const ScoreCircle = ({ pct, size = 76, stroke = 8 }) => {
  const r = (size - stroke) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  const color = scoreColor(pct);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={c} cy={c} r={r} fill="none" stroke={grey(200)} strokeWidth={stroke} />
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={circ}
        strokeDashoffset={circ * (1 - pct / 100)}
        strokeLinecap="round"
        transform={`rotate(-90 ${c} ${c})`}
      />
      <text x={c} y={c} textAnchor="middle" dominantBaseline="central" fontSize={size * 0.24} fontWeight={800} fill={grey(800)}>{`${pct}%`}</text>
    </svg>
  );
};

const StatutIcon = ({ statut, sx }) => {
  if (statut === 'conforme') return <CheckCircleRounded sx={sx} />;
  if (statut === 'non_conforme') return <CancelRounded sx={sx} />;
  if (statut === 'partiellement') return <ErrorRounded sx={sx} />;
  return <RemoveCircleOutlineRounded sx={sx} />;
};

const Badge = ({ label, bg, color }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: 0.75, py: 0.15, borderRadius: '6px', backgroundColor: bg, color, fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
    {label}
  </Box>
);

// Badges d'un indicateur : source (Auto/Mixte, pas Manuel) + périmètre (CFA / Certifiant).
const IndicateurBadges = ({ indic }) => (
  <>
    {indic.source !== 'manuel' && <Badge label={SOURCE_LABEL[indic.source]} bg={SOURCE_COULEUR[indic.source].bg} color={SOURCE_COULEUR[indic.source].color} />}
    {indic.cfa_only && <Badge label="CFA" bg={getTone('amber').bgStrong} color={getTone('amber').color} />}
    {indic.certifiant_only && <Badge label="Certifiant" bg={getTone('emerald').bgStrong} color={getTone('emerald').deep} />}
  </>
);

const StatutPill = ({ statut }) => {
  const c = STATUT_COULEUR[statut] || STATUT_COULEUR.non_evalue;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: '999px', backgroundColor: c.bg, flexShrink: 0 }}>
      <StatutIcon statut={statut} sx={{ fontSize: '0.95rem', color: c.color }} />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: c.color, whiteSpace: 'nowrap' }}>{STATUT_LABEL[statut]}</Typography>
    </Box>
  );
};

const modalLabelSx = { fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary', mb: 0.9 };
const outlineBtnSx = { textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', borderRadius: '9px', color: 'text.secondary', borderColor: 'divider', px: 1.25, '&:hover': { borderColor: 'divider', backgroundColor: (theme) => alpha(theme.palette.text.disabled, 0.08) } };

// ── Modale d'un indicateur ──
const IndicateurModal = ({ indic, evalRow, preuves, onClose, onStatut, onNotes, onUpload, onAddUrl, onDeletePreuve, onOpenPreuve }) => {
  const [notes, setNotesLocal] = useState(evalRow?.notes || '');
  const [urlNom, setUrlNom] = useState('');
  const [urlVal, setUrlVal] = useState('');
  const [showUrl, setShowUrl] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const statut = evalRow?.statut || 'non_evalue';
  const critere = CRITERES.find((c) => c.id === indic?.critere);

  useEffect(() => { setNotesLocal(evalRow?.notes || ''); setShowUrl(false); }, [indic?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!indic) return null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    await onUpload(indic.id, file);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleAddUrl = async () => {
    if (!urlVal.trim()) return;
    setBusy(true);
    await onAddUrl(indic.id, urlNom.trim() || urlVal.trim(), urlVal.trim());
    setBusy(false);
    setUrlNom('');
    setUrlVal('');
    setShowUrl(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.75, pr: 6, pb: 1.5 }}>
        <Box sx={{ width: 46, height: 46, borderRadius: '50%', backgroundColor: getTone('orange').light, color: getTone('orange').deepest, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '1.15rem', flexShrink: 0 }}>{indic.id}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: getTone('orange').base }}>{`Critère ${indic.critere} — ${critere ? critere.titre : ''}`}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: 'text.primary', lineHeight: 1.25, mt: 0.2 }}>{indic.libelle}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseRounded /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {/* Exigence */}
        <Typography sx={{ fontSize: '0.88rem', color: 'text.primary', lineHeight: 1.6 }}>{indic.description}</Typography>

        {/* Preuves attendues */}
        <Box sx={{ p: 1.75, borderRadius: '12px', backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.06), border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.14)}` }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'secondary.dark', mb: 0.75 }}>Preuves attendues en audit</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.84rem', color: 'text.secondary', mb: 0.35 } }}>
            {indic.preuves.map((p) => <li key={p}>{p}</li>)}
          </Box>
          {indic.source_hint && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1, pt: 1, borderTop: (theme) => `1px dashed ${alpha(theme.palette.secondary.main, 0.2)}` }}>
              <AutoAwesomeRounded sx={{ fontSize: '0.95rem', color: 'secondary.main', mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.76rem', color: 'secondary.dark', fontWeight: 600 }}>{`Source SYNDAYS : ${indic.source_hint}`}</Typography>
            </Box>
          )}
        </Box>

        {/* Statut */}
        <Box>
          <Typography sx={modalLabelSx}>Statut de conformité</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
            {STATUTS.map((s) => {
              const active = statut === s;
              const c = STATUT_COULEUR[s];
              return (
                <Box
                  component="button"
                  type="button"
                  key={s}
                  onClick={() => onStatut(indic.id, s)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, textAlign: 'center',
                    minHeight: 54, px: 0.75, py: 0.75, borderRadius: '11px', cursor: 'pointer',
                    border: '1px solid', borderColor: active ? c.color : 'divider',
                    backgroundColor: active ? c.bg : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: c.color, backgroundColor: c.bg }
                  }}
                >
                  <StatutIcon statut={s} sx={{ fontSize: '1.1rem', color: c.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.73rem', fontWeight: 700, lineHeight: 1.15, color: active ? c.color : 'text.secondary' }}>{STATUT_LABEL[s]}</Typography>
                </Box>
              );
            })}
          </Box>
          {evalRow?.date_verif && (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.75 }}>{`Vérifié le ${moment(evalRow.date_verif).format('DD/MM/YYYY')}`}</Typography>
          )}
        </Box>

        {/* Notes */}
        <Box>
          <Typography sx={modalLabelSx}>Notes d'audit interne</Typography>
          <TextField
            multiline minRows={2} fullWidth size="small" placeholder="Observations, plan d'amélioration, références internes…"
            value={notes}
            onChange={(e) => setNotesLocal(e.target.value)}
            onBlur={() => { if (notes !== (evalRow?.notes || '')) onNotes(indic.id, notes); }}
          />
        </Box>

        {/* Preuves attachées */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography sx={{ ...modalLabelSx, mb: 0 }}>{`Preuves attachées (${preuves.length})`}</Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <input ref={fileRef} type="file" hidden onChange={handleFile} />
              <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={13} /> : <UploadFileRounded sx={{ fontSize: '1rem' }} />} disabled={busy} onClick={() => fileRef.current?.click()} sx={outlineBtnSx}>Fichier</Button>
              <Button size="small" variant="outlined" startIcon={<LinkRounded sx={{ fontSize: '1rem' }} />} onClick={() => setShowUrl((v) => !v)} sx={outlineBtnSx}>URL</Button>
            </Box>
          </Box>

          {showUrl && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.25, flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Page du site web…" value={urlNom} onChange={(e) => setUrlNom(e.target.value)} sx={{ flex: '1 1 130px' }} />
              <TextField size="small" placeholder="https://…" value={urlVal} onChange={(e) => setUrlVal(e.target.value)} sx={{ flex: '2 1 160px' }} />
              <Button variant="contained" size="small" disabled={busy || !urlVal.trim()} onClick={handleAddUrl} sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: getTone('orange').base, '&:hover': { backgroundColor: getTone('orange').color } }}>Ajouter</Button>
            </Box>
          )}

          {preuves.length === 0 ? (
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>Aucune preuve attachée.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {preuves.map((p) => (
                <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
                  {p.type === 'url' ? <LinkRounded sx={{ fontSize: '1.05rem', color: 'text.secondary' }} /> : <UploadFileRounded sx={{ fontSize: '1.05rem', color: 'text.secondary' }} />}
                  <Typography noWrap sx={{ fontSize: '0.8rem', flex: 1, minWidth: 0 }} title={p.nom}>{p.nom}</Typography>
                  <Tooltip title="Ouvrir"><IconButton size="small" onClick={() => onOpenPreuve(p)}><OpenInNewRounded sx={{ fontSize: '1rem' }} /></IconButton></Tooltip>
                  <Tooltip title="Supprimer"><IconButton size="small" onClick={() => onDeletePreuve(p.id)}><DeleteOutlineRounded sx={{ fontSize: '1rem', color: 'error.main' }} /></IconButton></Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <VerifiedUserRounded sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{`Qualiopi · RNQ v9 (mars 2024) · Indicateur ${indic.id}`}</Typography>
        </Box>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '9px', backgroundColor: 'text.primary', '&:hover': { backgroundColor: 'text.primary' } }}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

const Qualiopi = () => {
  const mounted = useMounted();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ name: '', est_cfa: false, est_certifiant: false });
  const [evals, setEvals] = useState({});     // indicateur_id -> { statut, notes, date_verif }
  const [preuves, setPreuves] = useState({}); // indicateur_id -> [preuve]
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await qualiopiApi.getState(token());
      if (!mounted.current || !data) return;
      setProfile(data.profile || { name: '', est_cfa: false, est_certifiant: false });
      const ev = {};
      (data.evaluations || []).forEach((e) => { ev[e.indicateur_id] = { statut: e.statut, notes: e.notes, date_verif: e.date_verif }; });
      setEvals(ev);
      const pr = {};
      (data.preuves || []).forEach((p) => {
        if (!pr[p.indicateur_id]) pr[p.indicateur_id] = [];
        pr[p.indicateur_id].push(p);
      });
      setPreuves(pr);
    } catch (err) {
      console.error(err);
      toast.error('Impossible de charger la conformité Qualiopi.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [mounted]);

  useEffect(() => { load(); }, [load]);

  const indicateurs = indicateursApplicables({ isCfa: profile.est_cfa, isCertifiant: profile.est_certifiant });
  const statutMap = new Map();
  Object.entries(evals).forEach(([k, v]) => statutMap.set(Number(k), v.statut));
  const scores = scoresParCritere(indicateurs, statutMap);
  const scoreParCritereId = new Map(scores.map((s) => [s.critere, s]));
  const global = scoreGlobal(indicateurs, statutMap);

  const toggleFlag = async (key) => {
    const next = { ...profile, [key]: !profile[key] };
    setProfile(next); // optimiste
    try {
      await qualiopiApi.setProfile(next.est_cfa, next.est_certifiant, token());
    } catch (err) {
      console.error(err);
      setProfile(profile);
      toast.error('Échec de la mise à jour du profil.');
    }
  };

  const setStatut = async (indicateurId, statut) => {
    const prev = evals[indicateurId];
    setEvals((e) => ({ ...e, [indicateurId]: { statut, notes: prev?.notes || '', date_verif: new Date().toISOString() } }));
    try {
      await qualiopiApi.setStatut(indicateurId, statut, token());
    } catch (err) {
      console.error(err);
      toast.error('Échec de l\'enregistrement du statut.');
    }
  };

  const setNotes = async (indicateurId, notes) => {
    const prev = evals[indicateurId];
    setEvals((e) => ({ ...e, [indicateurId]: { statut: prev?.statut || 'non_evalue', notes, date_verif: prev?.date_verif } }));
    try {
      await qualiopiApi.setNotes(indicateurId, notes, token());
    } catch (err) {
      console.error(err);
      toast.error('Échec de l\'enregistrement des notes.');
    }
  };

  const uploadPreuve = async (indicateurId, file) => {
    try {
      const res = await qualiopiApi.uploadPreuve(indicateurId, file, token());
      if (res?.status && res.status >= 400) throw new Error(res.message);
      await load();
      toast.success('Preuve ajoutée.');
    } catch (err) {
      console.error(err);
      toast.error('Échec du téléversement.');
    }
  };

  const addPreuveUrl = async (indicateurId, nom, url) => {
    try {
      await qualiopiApi.addPreuveUrl(indicateurId, nom, url, token());
      await load();
      toast.success('Lien ajouté.');
    } catch (err) {
      console.error(err);
      toast.error('Échec de l\'ajout du lien.');
    }
  };

  const deletePreuve = async (preuveId) => {
    try {
      await qualiopiApi.deletePreuve(preuveId, token());
      await load();
    } catch (err) {
      console.error(err);
      toast.error('Échec de la suppression.');
    }
  };

  const openPreuve = async (p) => {
    if (p.type === 'url' && p.url) { window.open(p.url, '_blank', 'noopener'); return; }
    try {
      const resp = await fetch(qualiopiApi.downloadPreuveUrl(p.id), { headers: { Authorization: `Bearer ${token()}` } });
      const blob = await resp.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch (err) {
      console.error(err);
      toast.error('Impossible d\'ouvrir la preuve.');
    }
  };

  const profilSuffix = profile.est_cfa && profile.est_certifiant ? ' (CFA certifiant)'
    : profile.est_certifiant ? ' (certifiant)'
      : profile.est_cfa ? ' (CFA)' : '';

  const openIndic = openId ? INDICATEURS.find((i) => i.id === openId) : null;

  return (
    <>
      <Helmet><title>Conformité Qualiopi | Syndays</title></Helmet>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 4 }}>
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>
          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: (t) => (t.palette.mode === 'light' ? greyAlpha(200, 0.8) : lightVeil(0.05)),
              background: (t) => (t.palette.mode === 'light'
                ? `linear-gradient(135deg, ${lightVeil(0.9)} 0%, ${greyAlpha(100, 0.7)} 100%)`
                : `linear-gradient(135deg, ${greyAlpha(900, 0.6)} 0%, ${greyAlpha(800, 0.4)} 100%)`),
              backdropFilter: 'blur(20px)',
              boxShadow: (t) => (t.palette.mode === 'light' ? `0 4px 24px ${darkVeil(0.04)}` : `0 4px 24px ${darkVeil(0.2)}`),
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.main} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.success.main, 0.3)}`,
                  flexShrink: 0
                }}
              >
                <VerifiedUserRounded sx={{ color: grey(0), fontSize: '1.7rem' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Conformité Qualiopi</Typography>
                <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', mt: 0.75 }}>
                  {`Référentiel National Qualité — ${indicateurs.length} indicateurs applicables à ${profile.name || 'l\'organisme'}${profilSuffix}.`}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Référentiel v9 (mars 2024) · 7 critères · 32 indicateurs</Typography>
              <Card sx={{ display: 'flex', px: 1, borderRadius: '10px', border: '1px solid', borderColor: 'divider', boxShadow: 'none', backgroundColor: (t) => (t.palette.mode === 'light' ? lightVeil(0.6) : 'transparent') }}>
                <FormControlLabel control={<Checkbox size="small" checked={profile.est_certifiant} onChange={() => toggleFlag('est_certifiant')} />} label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Certifiant</Typography>} />
                <FormControlLabel control={<Checkbox size="small" checked={profile.est_cfa} onChange={() => toggleFlag('est_cfa')} />} label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>CFA</Typography>} />
              </Card>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Bandeau scores */}
              <Card sx={{ p: 2.5, mb: 3, borderRadius: '16px', border: '1px solid', borderColor: 'divider', boxShadow: (theme) => `0 4px 20px -2px ${alpha(theme.palette.common.black, 0.04)}` }}>
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 2, minWidth: 200 }}>
                    <ScoreCircle pct={global.pct} />
                    <Box>
                      <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'text.secondary' }}>Score global</Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: 'text.primary', lineHeight: 1.1 }}>
                        {global.conformes}<Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>{` / ${global.total}`}</Box>
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>indicateurs conformes</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' }, gap: 1, flex: 1 }}>
                    {CRITERES.map((c) => {
                      const sc = scoreParCritereId.get(c.id) || { conformes: 0, total: 0 };
                      return (
                        <Box key={c.id} sx={{ p: 1.25, borderRadius: '12px', border: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'text.secondary' }}>{`Critère ${c.id}`}</Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: scoreColor(sc.total ? Math.round((sc.conformes / sc.total) * 100) : 0), lineHeight: 1.2 }}>
                            {sc.conformes}<Box component="span" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.85rem' }}>{`/${sc.total}`}</Box>
                          </Typography>
                          <Typography noWrap sx={{ fontSize: '0.68rem', color: 'text.secondary' }} title={c.titre}>{c.titre}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Card>

              {/* Liste par critère */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {CRITERES.map((c) => {
                  const list = indicateurs.filter((i) => i.critere === c.id);
                  if (list.length === 0) return null;
                  return (
                    <Card key={c.id} sx={{ borderRadius: '16px', border: '1px solid', borderColor: 'divider', boxShadow: (theme) => `0 4px 20px -2px ${alpha(theme.palette.common.black, 0.04)}`, overflow: 'hidden' }}>
                      <Box sx={{ px: 2.5, py: 1.75, backgroundColor: (t) => (t.palette.mode === 'dark' ? lightVeil(0.03) : grey(50)), borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: getTone('orange').base }}>{`Critère ${c.id}`}</Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: 'text.primary' }}>{c.titre}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.3 }}>{c.libelle}</Typography>
                      </Box>
                      <Box>
                        {list.map((indic) => {
                          const st = evals[indic.id]?.statut || 'non_evalue';
                          const nbPreuves = (preuves[indic.id] || []).length;
                          return (
                            <Box
                              component="button"
                              type="button"
                              key={indic.id}
                              onClick={() => setOpenId(indic.id)}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', textAlign: 'left',
                                px: 2.5, py: 1.5, border: 'none', borderBottom: '1px solid', borderColor: 'divider',
                                background: 'transparent', cursor: 'pointer', '&:last-of-type': { borderBottom: 'none' },
                                '&:hover': { backgroundColor: (t) => (t.palette.mode === 'dark' ? lightVeil(0.03) : greyAlpha(900, 0.02)) }
                              }}
                            >
                              <Box sx={{ width: 26, height: 26, borderRadius: '7px', backgroundColor: (t) => (t.palette.mode === 'dark' ? lightVeil(0.06) : grey(100)), color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem', flexShrink: 0 }}>{indic.id}</Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'text.primary' }}>{indic.libelle}</Typography>
                                  <IndicateurBadges indic={indic} />
                                </Box>
                                <Typography noWrap sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.2 }}>{indic.description}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}>{`${nbPreuves} preuve${nbPreuves > 1 ? 's' : ''}`}</Typography>
                              <StatutPill statut={st} />
                            </Box>
                          );
                        })}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </>
          )}
        </Container>
      </Box>

      {openIndic && (
        <IndicateurModal
          indic={openIndic}
          evalRow={evals[openIndic.id]}
          preuves={preuves[openIndic.id] || []}
          onClose={() => setOpenId(null)}
          onStatut={setStatut}
          onNotes={setNotes}
          onUpload={uploadPreuve}
          onAddUrl={addPreuveUrl}
          onDeletePreuve={deletePreuve}
          onOpenPreuve={openPreuve}
        />
      )}
    </>
  );
};

export default Qualiopi;
